import { inject, Injectable } from '@angular/core';
import { APIService } from '../APIService/api.service';
import jwtDecode from 'jwt-decode';
import { BehaviorSubject, firstValueFrom, Observable, throwError, of } from 'rxjs';
import { map, catchError, first, tap, timeout } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthNewService } from './auth.new.service';
import OktaAuth, { UserClaims } from '@okta/okta-auth-js';
import { OKTA_AUTH } from '@okta/okta-angular';
import dayjs from 'dayjs';

import * as CryptoJS from 'crypto-js';
import { MsalConfigService } from '../msal-config.service';
import { dbS } from '../_offline/StorageDexie';
import { IsRep } from '../_offline/dexieDB';
import { ToastrService } from 'ngx-toastr';
import { Logoutinterceptor } from '../LogoutInterceptor/logoutinterceptor';
import { OpenRepProgressService } from 'src/app/content/_components/_modals/replikat-progress/open-rep-progress.service';
import { signal } from '@angular/core';
import { log } from 'console';

export enum LoginMethod {
  Email,
  Okta,
  Microsoft,
}

export interface AuthState {
  isAuthenticated: boolean;
  permissionsLoaded: boolean;
  permissions: any;
  user: any;
  token: any;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  oktaAuth = inject<OktaAuth>(OKTA_AUTH);
  private apiservice = inject(APIService);
  private msalConfigService = inject(MsalConfigService);
  private router = inject(Router);
  private authNewService = inject(AuthNewService);
  private toastr = inject(ToastrService);

  protected readonly openRepProgress = inject(OpenRepProgressService);

  loggedin: BehaviorSubject<boolean>;
  token;
  $token: BehaviorSubject<any>;
  redirectURL: string;
  twofa: BehaviorSubject<number>;
  tempdata;
  hasmap = false;
  clientId = crypto.randomUUID();
  sid?: string;
  licensetype: BehaviorSubject<number>;

  private _loginMethod: LoginMethod | undefined;

  // Signal for reactive loginMethod access (for computed signals in components)
  public readonly loginMethod$ = signal<LoginMethod | undefined>(undefined);

  // New AuthState for reactive permissions
  private authStateSubject = new BehaviorSubject<AuthState>({
    isAuthenticated: false,
    permissionsLoaded: false,
    permissions: {},
    user: null,
    token: null,
  });

  public authState$ = this.authStateSubject.asObservable();
  constructor() {
    this.loggedin = new BehaviorSubject(false);
    this.$token = new BehaviorSubject({});
    this.loggedin.next(this.isLoggedIn());
    this.twofa = new BehaviorSubject(0);
    this.licensetype = new BehaviorSubject(0);
    // Make cleanup methods available globally for debugging/testing
    if (typeof window !== 'undefined') {
      (window as any).authServiceEmergencyCleanup = () => (this as any).emergencyCleanup?.();
      (window as any).authServiceClearAll = () => this.clearAllAuthenticationData();
      (window as any).authServiceLogout = () => this.logout();
    }
  }

  public getRight(mode: string, key: string, otypid?: number) {
    if (this.token && this.token.rights && this.token.rights[mode]) {
      let searchArr = this.token.rights[mode];
      if (mode == ' type' && otypid != undefined) {
        for (let i = 0; i < searchArr.length; i++) {
          const typeRights = searchArr[i];
          if (typeRights.OTYPID == otypid) {
            searchArr = typeRights.rights;
            break;
          }
        }
      }

      for (let e = 0; e < searchArr.length; e++) {
        const right = searchArr[e];
        if (right.key == key) {
          return right.value.toString() === 'true';
        }
      }
      return undefined;
    }
  }

  public get loginMethod() {
    return this._loginMethod;
  }

  public set loginMethod(loginMethod: LoginMethod) {
    this._loginMethod = loginMethod;
    this.loginMethod$.set(loginMethod);
  }

  /**
   * Returns true when user is logged in via Okta and has only external/customer groups
   * (no internal or admin groups). Used to limit UI for external customers.
   */
  public isExternalOnlyOktaUser(): boolean {
    // Must be Okta login
    if (this._loginMethod !== LoginMethod.Okta) {
      return false;
    }

    const groups = this.token?.groups;
    if (!Array.isArray(groups) || groups.length === 0) {
      return false;
    }

    // Check if user has any internal or admin groups
    const hasInternalOrAdmin = groups.some((g: any) => {
      const groupType = g?.GroupType?.toLowerCase();
      const groupName = (g?.GroupName || g?.name || '')?.toLowerCase();

      // Check by GroupType if available
      if (groupType === 'internal' || groupType === 'admin') {
        return true;
      }

      // Fallback: check group name patterns
      if (groupName.includes('admin') || groupName.includes('internal') || groupName.includes('design')) {
        return true;
      }

      return false;
    });

    // Check if user has at least one external group
    const hasExternal = groups.some((g: any) => {
      const groupType = g?.GroupType?.toLowerCase();
      const groupName = (g?.GroupName || g?.name || '')?.toLowerCase();

      if (groupType === 'external') {
        return true;
      }

      // Fallback: check group name patterns for customer/external
      if (groupName.includes('customer') || groupName.includes('external') || groupName.includes('kunde')) {
        return true;
      }

      return false;
    });

    // External-only = has external groups AND no internal/admin groups
    return hasExternal && !hasInternalOrAdmin;
  }

  public checkemail(email: string) {
    return this.apiservice.checkmail(email).pipe(
      first(),
      map((data: any) => {
        return data;
      }),
      catchError((error: any) => new BehaviorSubject(false))
    );
  }

  public updatepassword(email: string, userid: string, pw: string) {
    return this.apiservice.updatepassword(email, userid, pw).pipe(
      first(),
      map((data: any) => {
        return data;
      }),
      catchError((error: any) => new BehaviorSubject(false))
    );
  }

  public getOktaMRproToken(oktaLoginRequest: any) {
    return this.apiservice.getOktaMRproToken(oktaLoginRequest).pipe(
      first(),
      map((data: any) => {
        return data;
      }),
      catchError((error: any) => {
        // Re-throw the error instead of swallowing it
        throw error;
      })
    );
  }

  public sendforgotemail(email: string, code: string) {
    // Updated to use new secure password reset flow
    return this.apiservice.sendforgotemail(email, code).pipe(
      first(),
      map((data: any) => {
        return data;
      }),
      catchError((error: any) => {
        // Return the response even on error for neutral messaging
        return new BehaviorSubject({ message: 'Email sent if account exists' });
      })
    );
  }

  public loginGetDatabases(email: string, pwhash: string) {
    const res = this.apiservice.loginGetDatabases(email, pwhash).pipe(
      first(),
      map((data: any) => {
        this._loginMethod = LoginMethod.Email;
        return data;
      }),
      // Statt new BehaviorSubject(error) besser:
      catchError((error: any) => throwError(() => error))
    );

    return res;
  }

  public login(email: string, pwhash: string, db: any, karteFileID: string) {
    return this.apiservice.login(email, pwhash, db, karteFileID, this.clientId).pipe(
      first(),
      map((data: any) => {
        this._loginMethod = LoginMethod.Email;
        return this.handleLogin(data);
      }),
      catchError((error: any) => new BehaviorSubject(error))
    );
  }

  public internlogin(email: string, pwhash: string, domian: string, karteFileID: string) {
    return this.apiservice.internlogin(email, pwhash, domian, karteFileID).pipe(
      first(),
      map((data: any) => {
        this._loginMethod = LoginMethod.Email;
        return data;
      }),
      catchError((error: any) => new BehaviorSubject(error))
    );
  }

  public sendHeartbeat() {
    //console.log('🔧 [AUTH-SERVICE] sendHeartbeat', "");
    if (this.licensetype.value == 0) {
      if (localStorage.getItem('licensetype') != null) {
        this.licensetype.next(parseInt(localStorage.getItem('licensetype')));
      }
    }
    if (this.licensetype.value == 0) {
      return new BehaviorSubject('');
    } else {
      return this.apiservice.sendHeartbeat().pipe(
        first(),
        map((data: any) => {
          return data;
        }),
        catchError((error: any) => new BehaviorSubject(error))
      );
    }
  }

  public send2FASMS(mobil: string, code: string) {
    return this.apiservice.send2FASMS(mobil, code).pipe(
      first(),
      map((data: any) => {
        return data;
      }),
      catchError((error: any) => new BehaviorSubject(error))
    );
  }

  public async oktaLogin(claims: UserClaims) {
    const oktaLoginRequest = {
      email: claims.email,
      username: claims.preferred_username || claims.name,
      givenName: claims.given_name,
      familyName: claims.family_name,
      usergruppen: claims.groups || [],
    };

    try {
      const data = await firstValueFrom(this.getOktaMRproToken(oktaLoginRequest));
      this._loginMethod = LoginMethod.Okta;
      return this.handleLogin(data);
    } catch (error) {
      throw error;
    }
  }

  public msalLogin(email: string, token: string): Observable<any> {
    return this.apiservice.msalLogin(email, token).pipe(
      tap(data => {
        this._loginMethod = LoginMethod.Microsoft;
        this.handleLogin(data);
      }),
      catchError(error => {
        console.error('[MSAL] API Login failed:', error);
        return throwError(() => error); // Ensure error propagates to the caller
      })
    );
  }

  public refreshLogin(email: string, refreshToken: string) {
    return this.apiservice.refreshLogin(email, refreshToken, this.sid!, this.clientId).pipe(
      first(),
      map((data: any) => {
        this.handleLogin(data);
      }),
      catchError((error: any) => new BehaviorSubject(false))
    );
  }

  public handle2FALogin() {
    let data = this.tempdata;

    localStorage.setItem('jwt', data.token);
    localStorage.setItem('rights', this.encrypt(JSON.stringify(data.rights)));
    localStorage.setItem('hasMap', this.encrypt(data.hasMap.toString()));
    this.hasmap = !!data.hasMap; // Convert to boolean - handles true, 'true', 1, etc.
    console.log('🔧 [AUTH-SERVICE] 2FA hasMap from backend:', data.hasMap, '-> this.hasmap:', this.hasmap);
    localStorage.setItem('auftraglaenge', this.encrypt(data.auftraglaenge));
    localStorage.setItem('optInterface', this.encrypt(data.optInterface));
    localStorage.setItem('jwt_exp', data.expiresAt);

    this.token = jwtDecode(data.token);

    // Set additional required localStorage items for proper initialization
    if (this.token.email) {
      sessionStorage.setItem('User', this.token.email);
    }
    if (this.token.db) {
      localStorage.setItem('DB', this.token.db);
      // Format the database name for display
      const dbName = this.token.db.substring(6).replace('_test', ' (Testdatenbank)').replace('_', ' ');
      const formatDBName = dbName
        .replace(new RegExp('\\b\\w{1,3}\\b', 'g'), str => str.toUpperCase())
        .replace(new RegExp('(^|\\s)[a-z]', 'g'), str => str.toUpperCase());
      localStorage.setItem('FormatCurDB', formatDBName);
    }

    this.$token.next(this.token);
    this.loggedin.next(true);

    // Update AuthState with loaded permissions
    this.authStateSubject.next({
      isAuthenticated: true,
      permissionsLoaded: true,
      permissions: data.rights || {},
      user: this.token,
      token: this.token,
    });

    if (data.dbintern) localStorage.setItem('intdb', data.dbintern);
    else localStorage.setItem('intdb', '');

    if (data.image) localStorage.setItem('userimg', data.image);

    // Navigate to appropriate page after successful 2FA login
    this.navigateAfterLogin();
  }

  public handleLogin(data) {
    if (data.refresh) {
      localStorage.setItem('refresh', data.refresh.refreshToken);
    }
    if (data.token) {
      //this.twofa = data.twofa;
      this.twofa.next(data.twofa);
      localStorage.setItem('twofa', data.twofa);

      if (this.twofa.value == 1 || this.twofa.value == 2) {
        this.tempdata = data;
        return true;
      }

      localStorage.setItem('jwt', data.token);
      localStorage.setItem('rights', this.encrypt(JSON.stringify(data.rights)));
      localStorage.setItem('hasMap', this.encrypt(data.hasMap.toString()));
      this.hasmap = !!data.hasMap; // Convert to boolean - handles true, 'true', 1, etc.
      localStorage.setItem('auftraglaenge', this.encrypt(data.auftraglaenge));
      localStorage.setItem('optInterface', this.encrypt(data.optInterface));
      localStorage.setItem('jwt_exp', data.expiresAt);

      if (data.image) {
        localStorage.setItem('userimg', data.image);
      }

      this.token = jwtDecode(data.token);
      //this.token.rights = JSON.parse(this.token.rights);

      // Set additional required localStorage items for proper initialization
      if (this.token.email) {
        sessionStorage.setItem('User', this.token.email);
      }

      this.licensetype.next(this.token.licensetype);
      localStorage.setItem('licensetype', this.token.licensetype);

      if (this.token.sessionId) {
        this.sid = this.token.sessionId;
      }

      if (this.token.db) {
        localStorage.setItem('DB', this.token.db);
        // Format the database name for display
        const dbName = this.token.db.substring(6).replace('_test', ' (Testdatenbank)').replace('_', ' ');
        const formatDBName = dbName
          .replace(new RegExp('\\b\\w{1,3}\\b', 'g'), str => str.toUpperCase())
          .replace(new RegExp('(^|\\s)[a-z]', 'g'), str => str.toUpperCase());
        localStorage.setItem('FormatCurDB', formatDBName);
      }

      if (!data.rights.dev) {
        if (data.dexieDB) {
          if (data.toReplicate?.length) {
            this.openRepProgress.setupConfig.set({
              IDs: data.toReplicate,
              repId: +data.dexieDB,
            });
            this.openRepProgress.isRepProgressModalOpen.set(true);
          } else {
            localStorage.setItem('repID', data.dexieDB);
            localStorage.setItem('repDB', localStorage.getItem('intdb') || localStorage.getItem('DB'));
          }
        } else {
          localStorage.removeItem('repDB');
        }
      } else {
        //Falls interner User im Replikat und die REPID passt, REPDB setzen
        if (data.dexieDB && localStorage.getItem('repID') == data.dexieDB)
          localStorage.setItem('repDB', localStorage.getItem('DB'));
        else localStorage.removeItem('repDB');
      }

      this.$token.next(this.token);
      this.loggedin.next(true);

      // Update AuthState with loaded permissions
      this.authStateSubject.next({
        isAuthenticated: true,
        permissionsLoaded: true,
        permissions: data.rights || {},
        user: this.token,
        token: this.token,
      });

      if (data.dbintern) {
        localStorage.setItem('intdb', data.dbintern);
      } else {
        localStorage.setItem('intdb', '');
      }

      //Prüfen ob es was gibt im Localstorage. Wenn nicht neu anlegen - beim nächsten mal wird es geladen.
      // dbS.getGISDate(localStorage.getItem("DB") +  localStorage.getItem("karteFileID")).then(x => {
      //   if (!x) {
      //     this.apiservice
      //       .refreshTopoJson(1, 0, 0)
      //       .subscribe((res: any) => {
      //         dbS.saveIntoGIS(res, localStorage.getItem("DB"), parseInt(localStorage.getItem("karteFileID")))
      //       });
      //   }
      // })

      // Reset session expired flags to prevent multiple toasters
      Logoutinterceptor.resetSessionExpiredFlags();

      this.navigateAfterLogin();

      return true;
    }

    if (data.error) {
      return data;
    }
    if (data.info) {
      return data;
    }

    //this.apiservice.refreshJWT();
  }

  private isLoggingOut = false;

  public async logout() {
    // Prevent multiple simultaneous logout calls
    if (this.isLoggingOut) {
      console.log('Logout already in progress, skipping...');
      return;
    }

    this.isLoggingOut = true;
    console.log('Starting logout process...');

    this.apiservice.sendRelease().subscribe();

    // Call API logout endpoint with proper error handling
    try {
      await firstValueFrom(
        this.apiservice.logout().pipe(
          first(),
          timeout(5000), // 5 second timeout
          catchError(error => {
            console.warn('Server logout failed (this is not critical):', error);
            // Continue with client-side logout even if server logout fails
            return of({ success: false, error: error });
          })
        )
      );
      console.log('Server logout completed');
    } catch (error) {
      console.warn('Server logout error (continuing with client-side logout):', error);
    }

    // Remove beforeunload handler
    window.onbeforeunload = function () {};

    // Determine which logout method to use based on current login method
    const currentLoginMethod = this._loginMethod;
    console.log('Current login method:', currentLoginMethod);

    try {
      if (currentLoginMethod == LoginMethod.Okta || (await this.oktaAuth.isAuthenticated())) {
        console.log('Performing Okta logout');
        await this.oktaLogoutFromAppOnly();
      } else if (currentLoginMethod == LoginMethod.Microsoft) {
        console.log('Performing Microsoft logout');
        await this.msalLogoutFromApp();
      } else {
        console.log('Performing standard logout');
        // Use centralized cleanup method to clear ALL authentication data
        this.clearAllAuthenticationData();

        // Clear general cookies (comprehensive)
        const generalCookies = [
          'Token-Data',
          'Rights-Data',
          'Group-Data',
          'HasMap-Data',
          'DT',
          'JSESSIONID',
          'idx',
          'srefresh',
          'proximity_2f6b7d15d3d2',
          'luf_819515995',
          'ln',
          'optUseFAT',
        ];

        generalCookies.forEach(cookieName => {
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname}`;
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname}`;
        });

        // Navigate to home/login
        this.router.navigateByUrl('/');

        // Force page reload to ensure clean state
        setTimeout(() => {
          console.log('Reloading page to ensure clean state');
          location.reload();
        }, 100);
      }
    } catch (error) {
      console.error('Error during logout process:', error);

      // Fallback: still perform cleanup even if specific logout fails
      this.clearAllAuthenticationData();
      this.router.navigateByUrl('/login');
      setTimeout(() => location.reload(), 100);
    } finally {
      this.isLoggingOut = false;
    }

    console.log('Logout process completed');
  }

  public isLoggedIn() {
    const exp = this.getExpiration();
    const hasValidJWT =
      localStorage.getItem('jwt') &&
      localStorage.getItem('jwt').trim() !== '' &&
      localStorage.getItem('jwt') !== 'null';

    // User is logged in if: (has valid JWT token AND expiration is in future) OR (has valid JWT token AND is in replica mode)
    let test = hasValidJWT && ((exp && dayjs().isBefore(exp)) || IsRep());
    if (test && !this.token) {
      let tokenFromLocalStorage = localStorage.getItem('jwt');
      if (tokenFromLocalStorage) {
        try {
          this.token = jwtDecode(tokenFromLocalStorage);

          // Load rights from localStorage if not present in token
          if (!this.token.rights) {
            const encryptedRights = localStorage.getItem('rights');
            if (encryptedRights) {
              try {
                this.token.rights = JSON.parse(this.decrypt(encryptedRights));
              } catch (error) {
                console.error('Error decrypting rights in isLoggedIn:', error);
                this.token.rights = {};
              }
            } else {
              this.token.rights = {};
            }
          }

          // Restore loginMethod from JWT token if not already set
          if (!this._loginMethod && this.token.loginMethod) {
            if (this.token.loginMethod === 'okta') {
              this._loginMethod = LoginMethod.Okta;
              this.loginMethod$.set(LoginMethod.Okta);
            } else if (this.token.loginMethod === 'microsoft') {
              this._loginMethod = LoginMethod.Microsoft;
              this.loginMethod$.set(LoginMethod.Microsoft);
            } else {
              this._loginMethod = LoginMethod.Email;
              this.loginMethod$.set(LoginMethod.Email);
            }
          }

          // Restore hasmap from localStorage if not already set
          if (!this.hasmap) {
            try {
              const encryptedHasMap = localStorage.getItem('hasMap');
              if (encryptedHasMap) {
                this.hasmap = this.decrypt(encryptedHasMap) === 'true';
              }
            } catch (error) {
              console.error('Error restoring hasmap in isLoggedIn:', error);
              this.hasmap = false;
            }
          }

          // Update AuthState when token is loaded from localStorage
          this.authStateSubject.next({
            isAuthenticated: true,
            permissionsLoaded: true,
            permissions: this.token.rights || {},
            user: this.token,
            token: this.token,
          });
        } catch (error) {
          console.error('jwtDecodeError:', error);
        }
        if (this.token) {
          this.$token.next(this.token);
        } else {
          this.$token.next({});
        }
      }
    }
    return test;
  }

  isLoginMethod(loginMethod: LoginMethod): boolean {
    return this._loginMethod === loginMethod && this.isLoggedIn();
  }

  public isMicrosoftLogin(): boolean {
    if (this._loginMethod === LoginMethod.Microsoft) {
      return true;
    }

    const storedLoginMethod = localStorage.getItem('loginMethod');
    if ((storedLoginMethod || '').toLowerCase() === 'microsoft') {
      return true;
    }

    return localStorage.getItem('msal') != null;
  }

  public canAccessGISMap(): boolean {
    if (this.isMicrosoftLogin()) {
      return false;
    }

    let hasMapFromStorage = false;
    try {
      const encryptedHasMap = localStorage.getItem('hasMap');
      if (encryptedHasMap) {
        hasMapFromStorage = this.decrypt(encryptedHasMap) === 'true';
      }
    } catch (error) {
      console.error('[NAV] Error reading hasMap from localStorage:', error);
      hasMapFromStorage = false;
    }

    const hasAllowVMAP = this.getRight('view', 'AllowVMAP');
    return hasAllowVMAP === true && hasMapFromStorage === true;
  }

  async oktaLogoutFromAppOnly(): Promise<void> {
    try {
      console.log('Starting Okta logout with IdP session termination...');

      // Ensure next Okta login shows the login screen explicitly once
      sessionStorage.setItem('oktaForceLogin', 'true');

      // Prefer closing the Okta browser session directly (no redirect, avoids 400 on post_logout_redirect_uri)
      try {
        await (this.oktaAuth as any).closeSession();
        console.log('Okta session closed via closeSession');
      } catch (csErr) {
        console.warn('closeSession failed, continuing with local cleanup:', csErr);
      }

      // Local cleanup and navigation back to login
      const preserveForce = sessionStorage.getItem('oktaForceLogin') === 'true';
      try {
        this.oktaAuth.tokenManager.clear();
      } catch {}
      try {
        await this.oktaAuth.stop();
      } catch {}
      this.clearAllAuthenticationData();
      // Restore the force-login flag after sessionStorage.clear() inside cleanup
      if (preserveForce) {
        sessionStorage.setItem('oktaForceLogin', 'true');
      }
      await this.oktaAuth.signOut();
      await this.router.navigateByUrl('/login');
    } catch (error) {
      console.error('Error during Okta signOut, falling back to local cleanup:', error);
      // Fallback: local cleanup only (IdP session may still exist)pfat
      const preserveForce = sessionStorage.getItem('oktaForceLogin') === 'true';
      try {
        this.oktaAuth.tokenManager.clear();
      } catch {}
      try {
        await this.oktaAuth.stop();
      } catch {}
      this.clearAllAuthenticationData();
      if (preserveForce) {
        sessionStorage.setItem('oktaForceLogin', 'true');
      }
      await this.router.navigateByUrl('/login');
    }
  }

  private clearAllAuthenticationData(): void {
    console.log('Starting comprehensive authentication data cleanup...');

    // Clear ALL localStorage authentication data (existing keys)
    const authKeys = [
      'jwt',
      'jwt_exp',
      'refresh',
      'rights',
      'userimg',
      'intdb',
      'token',
      'expiresAt',
      'db',
      'image',
      'hasMap',
      'auftraglaenge',
      'optInterface',
      'FormatCurDB',
      'loginMethod',
      'msal',
      'repDB',
      'DB',
    ];

    // Additional keys I can see from the screenshot and common auth patterns
    const additionalKeys = [
      'DT',
      'idx',
      'JSESSIONID',
      'ln',
      'luf_819515995',
      'proximity_2f6b7d15d3d2',
      'optUseFAT',
      // Okta related
      'okta-cache-storage',
      'okta-original-uri-storage',
      'okta-shared-transaction-storage',
      'okta-token-storage',
      'okta-token-storage_idToken',
      'okta-token-storage_accessToken',
      'okta-pkce-storage',
      'okta-transaction-storage',
      // MSAL related
      'msal.idtoken',
      'msal.client.info',
      'msal.error',
      'msal.state',
      'msal.interaction.status',
      'msal.account.keys',
      'msal.token.keys',
      // General auth related
      'access_token',
      'id_token',
      'refresh_token',
      'session_state',
      'user',
      'username',
      'email',
      'profile',
      'roles',
      'permissions',
      'auth_time',
      'exp',
      'iat',
      'sub',
      'aud',
      'iss',
      // Application specific that might contain auth data
      'currentUser',
      'authToken',
      'bearerToken',
      'apiKey',
    ];

    // Combine all keys
    const allAuthKeys = [...authKeys, ...additionalKeys];

    // Remove known keys
    allAuthKeys.forEach(key => {
      if (localStorage.getItem(key)) {
        console.log(`Removing localStorage key: ${key}`);
        localStorage.removeItem(key);
      }
    });

    // Comprehensive cleanup: scan all localStorage for auth-related patterns
    const localStorageKeys = Object.keys(localStorage);
    localStorageKeys.forEach(key => {
      // Remove any key that looks like auth data
      if (
        key.toLowerCase().includes('token') ||
        key.toLowerCase().includes('auth') ||
        key.toLowerCase().includes('okta') ||
        key.toLowerCase().includes('msal') ||
        key.toLowerCase().includes('jwt') ||
        key.toLowerCase().includes('session') ||
        key.toLowerCase().includes('user') ||
        key.toLowerCase().includes('login') ||
        key.toLowerCase().includes('refresh') ||
        key.toLowerCase().includes('rights') ||
        key.toLowerCase().includes('permission') ||
        key.toLowerCase().includes('role') ||
        key.toLowerCase().includes('credential') ||
        key.toLowerCase().includes('bearer') ||
        key.startsWith('proximity_') ||
        key.startsWith('luf_') ||
        key.match(/^[a-f0-9]{8,}$/i)
      ) {
        // Remove long hex strings that might be tokens
        console.log(`Removing localStorage key (pattern match): ${key}`);
        localStorage.removeItem(key);
      }
    });

    // Clear ALL sessionStorage
    console.log('Clearing all sessionStorage');
    sessionStorage.clear();

    // Clear IndexedDB entries that might contain auth data
    if ('indexedDB' in window) {
      try {
        // Common IndexedDB names used by auth libraries
        const dbNames = ['okta-cache-storage', 'msal-cache', 'auth-cache', 'user-cache'];
        dbNames.forEach(dbName => {
          const deleteReq = indexedDB.deleteDatabase(dbName);
          deleteReq.onsuccess = () => console.log(`Deleted IndexedDB: ${dbName}`);
          deleteReq.onerror = () => console.log(`Failed to delete IndexedDB: ${dbName}`);
        });
      } catch (error) {
        console.warn('Error clearing IndexedDB:', error);
      }
    }

    // Clear all cookies (comprehensive)
    console.log('Clearing all cookies');
    document.cookie.split(';').forEach(cookie => {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      if (name) {
        // Clear cookie for current domain and all possible paths
        const domains = [window.location.hostname, `.${window.location.hostname}`, 'localhost', '.localhost'];
        const paths = ['/', '/login', '/auth', '/oauth', '/callback'];

        domains.forEach(domain => {
          paths.forEach(path => {
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${domain}`;
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}`;
          });
        });
      }
    });

    // Reset service state
    this.token = undefined;
    this._loginMethod = undefined;
    this.loggedin.next(false);
    this.$token.next({});

    console.log('Authentication data cleanup completed');
  }

  // Emergency cleanup method - can be called manually for testing or troubleshooting
  public emergencyCleanup(): void {
    console.log('EMERGENCY CLEANUP: Clearing ALL browser storage data');

    // Clear everything in localStorage
    localStorage.clear();

    // Clear everything in sessionStorage
    sessionStorage.clear();

    // Clear all cookies
    document.cookie.split(';').forEach(cookie => {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      if (name) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname}`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname}`;
      }
    });

    // Reset service state
    this.token = undefined;
    this._loginMethod = undefined;
    this.loggedin.next(false);
    this.$token.next({});

    console.log('EMERGENCY CLEANUP: Completed');
  }

  async msalLogoutFromApp(): Promise<void> {
    await this.msalConfigService.clearMsalCache();

    // App-only logout: clear MRproCloud auth state without redirecting to Microsoft logout page
    this.clearAllAuthenticationData();

    await this.router.navigateByUrl('/login');

    // Ensure full in-app auth reset and guard reevaluation
    setTimeout(() => {
      location.reload();
    }, 100);
  }

  public getRefreshToken() {
    return {
      passphrase: localStorage.getItem('refresh'),
      expiresAt: parseInt(localStorage.getItem('jwt_exp')),
    };
  }

  public get$token() {
    var token = this.$token;
    try {
      token.value.rights = JSON.parse(this.decrypt(localStorage.getItem('rights')));
    } catch (error) {}
    return token;
  }

  public getToken() {
    if (!this.token) {
      try {
        const jwtToken = localStorage.getItem('jwt');

        // Check if token exists and is not empty
        if (!jwtToken || jwtToken.trim() === '' || jwtToken === 'null' || jwtToken === 'undefined') {
          console.warn('JWT token is empty or invalid in localStorage');
          this.token = null;
          return null;
        }

        // Basic JWT format validation
        const tokenParts = jwtToken.split('.');
        if (tokenParts.length !== 3) {
          console.warn('JWT token does not have proper format (expected 3 parts)');
          this.token = null;
          return null;
        }

        this.token = jwtDecode(jwtToken);

        // Validate that decoded token has required properties
        if (!this.token || typeof this.token !== 'object') {
          console.warn('Decoded JWT token is not a valid object');
          this.token = null;
          return null;
        }
      } catch (error) {
        console.error('JWT decode error in AuthService:', error);
        console.warn('Clearing invalid JWT token from localStorage');

        // Clear the invalid token to prevent repeated errors
        localStorage.removeItem('jwt');
        localStorage.removeItem('jwt_exp');
        localStorage.removeItem('refresh');
        this.token = null;
        return null;
      }
    }

    // Only proceed with rights and other operations if we have a valid token
    if (!this.token) {
      return null;
    }

    const readtwofa = localStorage.getItem('twofa');
    if (readtwofa) {
      this.twofa.next(parseInt(readtwofa));
    }

    if (!this.token?.rights) {
      const encryptedRights = localStorage.getItem('rights');
      if (encryptedRights) {
        try {
          this.token.rights = JSON.parse(this.decrypt(encryptedRights));
        } catch (error) {
          console.error('Error decrypting rights:', error);
          // If decryption fails, set empty rights object to prevent crashes
          this.token.rights = {};
        }
      } else {
        // If no rights in localStorage, set empty rights object
        this.token.rights = {};
      }
    }

    // Parse groups from JWT token if available
    if (!this.token?.groups && this.token?.group) {
      try {
        // The 'group' claim contains a JSON string with the groups array
        const groupsData = typeof this.token.group === 'string' ? JSON.parse(this.token.group) : this.token.group;

        if (Array.isArray(groupsData)) {
          this.token.groups = groupsData;
          // console.log('🔧 [DEBUG] Auth Service - Parsed groups from JWT token:', this.token.groups);
        } else {
          console.warn('Groups data in JWT token is not an array:', groupsData);
          this.token.groups = [];
        }
      } catch (error) {
        console.error('Error parsing groups from JWT token:', error);
        this.token.groups = [];
      }
    }

    // Parse loginMethod from JWT token if available
    if (this.token?.loginMethod && !this._loginMethod) {
      try {
        const loginMethodString = this.token.loginMethod;
        if (loginMethodString === 'okta') {
          this._loginMethod = LoginMethod.Okta;
          this.loginMethod$.set(LoginMethod.Okta);
          console.log('🔧 [DEBUG] Auth Service - Parsed loginMethod from JWT token: Okta');
        } else if (loginMethodString === 'msal' || loginMethodString === 'microsoft') {
          this._loginMethod = LoginMethod.Microsoft;
          this.loginMethod$.set(LoginMethod.Microsoft);
          console.log('🔧 [DEBUG] Auth Service - Parsed loginMethod from JWT token: Microsoft');
        } else if (loginMethodString === 'email') {
          this._loginMethod = LoginMethod.Email;
          this.loginMethod$.set(LoginMethod.Email);
          console.log('🔧 [DEBUG] Auth Service - Parsed loginMethod from JWT token: Email');
        } else {
          console.warn('Unknown loginMethod in JWT token:', loginMethodString);
        }
      } catch (error) {
        console.error('Error parsing loginMethod from JWT token:', error);
      }
    }

    // Ensure required localStorage items are set
    if (this.token && this.token.email && !sessionStorage.getItem('User')) {
      sessionStorage.setItem('User', this.token.email);
    }
    if (this.token && this.token.db && !localStorage.getItem('DB')) {
      localStorage.setItem('DB', this.token.db);
    }
    if (this.token && this.token.db && !localStorage.getItem('FormatCurDB')) {
      const dbName = this.token.db.substring(6).replace('_test', ' (Testdatenbank)').replace('_', ' ');
      const formatDBName = dbName
        .replace(new RegExp('\\b\\w{1,3}\\b', 'g'), str => str.toUpperCase())
        .replace(new RegExp('(^|\\s)[a-z]', 'g'), str => str.toUpperCase());
      localStorage.setItem('FormatCurDB', formatDBName);
    }

    return this.token;
  }

  public async refreshTokenTimer() {
    try {
      const val = await firstValueFrom(this.apiservice.resetRefreshToken());
      localStorage.setItem('jwt_exp', val.refresh.expiresAt);
      localStorage.setItem('refresh_exp', val.refresh.expiresAt);
      localStorage.setItem('jwt', val.token);
    } catch (er) {
      return console.error(er);
    }
  }
  public getLoggedIn() {
    return this.loggedin;
  }

  public getExpiration() {
    let exp = localStorage.getItem('jwt_exp');
    if (exp) return dayjs.unix(parseInt(exp));
    else return undefined;
  }

  public encrypt(txt: string): string {
    return CryptoJS.AES.encrypt(txt, 'uselessEnc12345612!').toString();
  }

  public decrypt(txtToDecrypt: string) {
    if (!txtToDecrypt) {
      return '';
    }
    try {
      return CryptoJS.AES.decrypt(txtToDecrypt, 'uselessEnc12345612!').toString(CryptoJS.enc.Utf8);
    } catch (error) {
      console.error('Decryption error:', error);
      return '';
    }
  }

  private navigateAfterLogin(): void {
    console.log('🧭 [NAV] navigateAfterLogin called');
    console.log('🧭 [NAV] redirectURL:', this.redirectURL);

    // Check if there's a redirect URL stored
    if (this.redirectURL && this.redirectURL !== '/login' && this.redirectURL !== '/' && this.redirectURL !== '') {
      console.log('🧭 [NAV] Redirecting to stored URL:', this.redirectURL);
      const url = this.redirectURL;
      this.redirectURL = null; // Clear the redirect URL
      this.router.navigateByUrl(url);
      return;
    }
    this.setRoute();
  }

  public setRoute() {
    
    // Determine best default route based on user permissions and token claims
    const token = this.getToken();

    console.log('[NAV] token present:', !!token);
    console.log('[NAV] token.rights present:', !!token?.rights);
    console.log('[NAV] token.rights.view present:', !!token?.rights?.view);
    const hasMapAccess = this.canAccessGISMap();
    console.log('[NAV] hasMapAccess (AllowVMAP === true && hasMapFromStorage === true):', hasMapAccess);

    if (hasMapAccess) 
      this.router.navigateByUrl('/karte');
    else if(this.getRight('view', 'AllowVANL'))
      this.router.navigateByUrl('/anlagen');
    else if(this.getRight('view', 'AllowVAuftr'))
      this.router.navigateByUrl('/auftraege');
    else if(this.getRight('view', 'AllowVMANG'))
      this.router.navigateByUrl('/maengel');
    else if(this.getRight('view', 'AllowVDASH'))
      this.router.navigateByUrl('/dashboard');
    else if(this.getRight('view', 'AllowVKAL'))
      this.router.navigateByUrl('/kalender');
    else if(this.getRight('view', 'AllowVWART'))
      this.router.navigateByUrl('/wartung');
    else if(this.getRight('view', 'AllowVBeg'))
      this.router.navigateByUrl('/begehung');
    else if(this.getRight('view', 'AllowVPOJM'))
      this.router.navigateByUrl('/projekt');
    else
      this.router.navigateByUrl('/empty');
  }
}
